import { createStore } from 'react-sweet-state';
import { cloneDeep, map, without } from 'lodash';

import { isApplicationModeInfinity } from 'src/data/system';
import {
  createWorkspace,
  deleteWorkspace,
  queryWorkspace,
  queryWorkspaceDefaultTovAnalyzerForTool,
  queryWorkspaces,
  updateWorkspace,
  updateWorkspaceAnalyzerTovDefault,
} from 'src/graphql/workspace';
import * as ls from 'src/lib/localData';
import { captureMessage } from 'src/lib/sentry';

const NAME = 'workspace-store';

const persistWorkspaceSelection = (workspaceId) => {
  try {
    ls.currentWorkspaceId.write(workspaceId);
  } catch (error) {
    captureMessage("Couldn't persist workspace selection", {
      level: 'warning',
      extra: {
        error: error.toString(),
        source: 'WorkspaceStore -> persistWorkspaceSelection',
      },
    });
  }
};

const INITIAL_STATE = {
  currentWorkspaces: [],
  loadingWorkspaces: false,
  removingWorkspace: false,
  workspacesFetched: false,

  customerId: null,

  loadingWorkspace: false,

  currentWorkspaceId: null,

  updatingWorkspaceAnalyzerTovDefault: false,
};

const prepareWorkspace = (workspace) => {
  let associatedFreestyleTools = workspace?.associatedFreestyleTools;
  try {
    associatedFreestyleTools = workspace?.associatedFreestyleTools.map((freestyleTool) => {
      return {
        ...freestyleTool,
        allInstructionsData: JSON.parse(freestyleTool.allInstructionsData || null),
      };
    });
  } catch {
    // do nothing
  }

  return {
    ...workspace,
    associatedFreestyleTools,
  };
};

const inferCurrentWorkspaceId = ({ workspaces, currentUserId }) => {
  if (!workspaces) {
    return null;
  }

  // In Extension We don't care about preserving "selected workspace" per tab - always use last selected per browser
  const shouldSkipSession = isApplicationModeInfinity();

  // Check if saved in LS workspace is in 'workspaces' list
  const lsWorkspaceId = ls.currentWorkspaceId.read({ skipSession: shouldSkipSession });
  const lsWorkspace = lsWorkspaceId ? workspaces.find((w) => String(w.id) === lsWorkspaceId) : null;

  // Check if provided list has a "default" workspace for the current user
  const userDefaultWorkspace = currentUserId
    ? workspaces.find((w) => (w.defaultForUserIds || []).includes(String(currentUserId)))
    : null;

  // Order of precedence: LS workspace -> user default workspace -> first workspace
  const currentWorkspace = lsWorkspace || userDefaultWorkspace || workspaces[0];
  return currentWorkspace?.id || null;
};

/**
 * Private actions
 */
export const PRIVATE_ACTIONS = {
  addWorkspaceToCache:
    (workspace) =>
    ({ getState, setState }) => {
      const preparedWorkspace = prepareWorkspace(workspace);
      const { currentWorkspaces: list } = getState();
      const newList = [preparedWorkspace, ...list];
      setState({ currentWorkspaces: newList });
    },

  mergeWorkspaceInCache:
    (workspace) =>
    ({ getState, setState }) => {
      const { currentWorkspaces } = getState();
      const preparedWorkspace = prepareWorkspace(workspace);

      const updatedWorkspaces = currentWorkspaces.reduce((acc, w) => {
        if (w.id === preparedWorkspace.id) {
          acc.push({ ...w, ...preparedWorkspace });
        } else {
          acc.push(w);
        }
        return acc;
      }, []);
      setState({ currentWorkspaces: updatedWorkspaces });
    },

  deleteWorkspaceFromCache:
    (workspace) =>
    ({ getState, setState }) => {
      const { currentWorkspaces: list } = getState();
      const newList = list.filter((w) => w.id !== workspace.id);
      setState({ currentWorkspaces: newList });
    },

  getWorkspace:
    (id) =>
    ({ getState }) => {
      const { currentWorkspaces } = getState();
      return currentWorkspaces.find((item) => item.id === id);
    },

  clearState:
    () =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },

  getWorkspaceDefaultTovAnalyzerForTool:
    (id) =>
    async ({ dispatch }, { currentCustomerId }) => {
      if (!currentCustomerId) {
        return;
      }

      try {
        const workspacesDefaultTovAnalyzer = await queryWorkspaceDefaultTovAnalyzerForTool({
          customerId: currentCustomerId,
          id,
        });

        if (workspacesDefaultTovAnalyzer) {
          dispatch(PRIVATE_ACTIONS.mergeWorkspaceInCache(workspacesDefaultTovAnalyzer));
        }
      } catch (error) {
        captureMessage('Error while calling "queryWorkspaceDefaultTovAnalyzerForTool" GQL query', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'WorkspaceStore -> getWorkspaceDefaultTovAnalyzerForTool',
          },
        });
      }
    },
};

/**
 * Public available actions
 */

export const ACTIONS = {
  init:
    (currentCustomerId) =>
    async ({ getState, setState, dispatch }, { notifications, currentUserId, isReviewer }) => {
      const { customerId, currentWorkspaceId: workspaceIdToPreserve } = getState();

      // A reviewer has no workspaces in the account.
      if (!currentCustomerId || isReviewer) {
        dispatch(PRIVATE_ACTIONS.clearState());
        return;
      }

      // in case customerId stayed the same we don't need to refetch workspaces
      if (customerId === currentCustomerId) {
        return;
      }

      // Clear most state but PRESERVE currentWorkspaceId temporarily.
      // This prevents the useMaintain* hooks from aborting in-flight requests
      // when switching customers via a project URL. We'll validate and potentially
      // update the workspace after fetching the new customer's workspace list.
      setState({
        currentWorkspaces: [],
        loadingWorkspaces: false,
        removingWorkspace: false,
        workspacesFetched: false,
        customerId: currentCustomerId,
        loadingWorkspace: false,
        updatingWorkspaceAnalyzerTovDefault: false,
        // Keep currentWorkspaceId for now - will validate after fetch
      });

      try {
        // Refresh workspaces list for the current customer
        const workspaces = await dispatch(ACTIONS.refreshWorkspaces());

        // Guard: Check if another init for a different customer started while we were fetching.
        // If so, abort - the other init will handle setting the correct state.
        const { customerId: currentStoredCustomerId } = getState();
        if (currentStoredCustomerId !== currentCustomerId) {
          return;
        }

        // Only now store the workspaces - after we've confirmed this init is still valid
        setState({ currentWorkspaces: workspaces });

        // Check if the preserved workspace exists in the new customer's workspace list.
        // This handles the case where user navigates to a project URL belonging to
        // a different customer - useQueryProject sets the workspace before init runs.
        const preservedWorkspaceExistsInNewCustomer =
          workspaceIdToPreserve && workspaces.some((w) => w.id === workspaceIdToPreserve);

        const workspaceIdToSelect = preservedWorkspaceExistsInNewCustomer
          ? workspaceIdToPreserve
          : inferCurrentWorkspaceId({ workspaces, currentUserId });

        if (workspaceIdToSelect) {
          // Always call selectCurrentWorkspace to ensure the workspace is set correctly.
          // We can't skip this even if workspaceIdToSelect === workspaceIdToPreserve because
          // another concurrent init (for a different customer) might have changed currentWorkspaceId
          // while we were fetching workspaces.
          await dispatch(ACTIONS.selectCurrentWorkspace(workspaceIdToSelect));
        } else {
          setState({ currentWorkspaceId: null });
        }
        setState({ workspacesFetched: true });
      } catch (error) {
        // Display notification to user
        notifications.displayWarning(
          'Network error',
          'Error fetching workspaces. Please try reloading the page.'
        );

        // Report "warning" to Sentry
        captureMessage('Error while calling "queryWorkspaces" GQL query', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'WorkspaceStore -> init',
          },
        });
      }
    },

  refreshWorkspaces:
    () =>
    async ({ setState }, { currentCustomerId }) => {
      setState({ loadingWorkspaces: true });

      try {
        const workspaces = await queryWorkspaces({ customerId: currentCustomerId });

        // Don't set currentWorkspaces here - let the caller (init) decide
        // whether to store them after checking if the customer is still valid.
        // This prevents race conditions where a stale init stores the wrong workspaces.
        return workspaces;
      } finally {
        setState({ loadingWorkspaces: false });
      }
    },

  updateWorkspace:
    (workspace) =>
    ({ dispatch }) => {
      return updateWorkspace(workspace).then((newWorkspace) => {
        dispatch(PRIVATE_ACTIONS.mergeWorkspaceInCache(newWorkspace));
        return newWorkspace;
      });
    },

  createWorkspace:
    (workspace) =>
    ({ dispatch }) => {
      return createWorkspace(workspace).then((newWorkspace) => {
        dispatch(PRIVATE_ACTIONS.addWorkspaceToCache(newWorkspace));

        dispatch(ACTIONS.selectCurrentWorkspace(newWorkspace.id));
      });
    },

  deleteWorkspace:
    (id) =>
    ({ getState, dispatch, setState }) => {
      const { currentWorkspaces, currentWorkspaceId } = getState();
      if (currentWorkspaceId === id) {
        // if user remove the current workspace set another workspace as the current
        const otherWorkspaces = currentWorkspaces.filter((workspace) => workspace.id !== id);
        dispatch(ACTIONS.selectCurrentWorkspace(otherWorkspaces[0].id)).catch((error) => {
          captureMessage("Couldn't select workspace", {
            level: 'warning',
            extra: {
              error: error.toString(),
              source: 'WorkspaceStore -> deleteWorkspace',
            },
          });
        });
      }
      setState({ removingWorkspace: true });
      return deleteWorkspace({ id }).then((deletedWorkspace) => {
        setState({ removingWorkspace: false });
        dispatch(PRIVATE_ACTIONS.deleteWorkspaceFromCache(deletedWorkspace));
        return deletedWorkspace;
      });
    },

  storeWorkspace:
    (workspace) =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.mergeWorkspaceInCache(workspace));
    },

  selectCurrentWorkspace:
    (id) =>
    async ({ dispatch, setState }) => {
      setState({ currentWorkspaceId: id });

      dispatch(PRIVATE_ACTIONS.getWorkspaceDefaultTovAnalyzerForTool(id));

      // Write selected ID to LS for future use
      persistWorkspaceSelection(id);
    },

  getCurrentWorkspace:
    () =>
    ({ getState }) => {
      const { currentWorkspaces, currentWorkspaceId } = getState();

      return currentWorkspaces.find((workspace) => workspace.id === currentWorkspaceId);
    },

  getCurrentWorkspaceId:
    () =>
    ({ getState }) => {
      return getState().currentWorkspaceId;
    },

  queryWorkspace:
    (id) =>
    async ({ setState }, { currentCustomerId }) => {
      setState({ loadingWorkspace: true });
      try {
        const workspace = await queryWorkspace({ customerId: currentCustomerId, id });
        return prepareWorkspace(workspace);
      } finally {
        setState({ loadingWorkspace: false });
      }
    },

  getCurrentWorkspaces:
    () =>
    ({ getState }) => {
      return getState().currentWorkspaces;
    },

  toggleWorkspaceDefaultForUserId:
    (workspaceId, userId) =>
    ({ getState, setState }) => {
      const { currentWorkspaces } = getState();
      const userIdString = String(userId);

      const selectedWorkspace = currentWorkspaces.find((workspace) => workspace.id === workspaceId);
      const workspaceIsDefaultForUser =
        selectedWorkspace?.defaultForUserIds.includes(userIdString) || false;

      const newList = currentWorkspaces.map((workspace) => {
        // Add/remove "default for this user id" from the requested workspace ...
        if (workspace.id === workspaceId) {
          return {
            ...workspace,
            defaultForUserIds: workspaceIsDefaultForUser
              ? without(selectedWorkspace.defaultForUserIds, userIdString)
              : [...selectedWorkspace.defaultForUserIds, userIdString],
          };

          // ... remove "default for this user id" in other workspaces, if it was just set for the requested one
        } else if (
          !workspaceIsDefaultForUser &&
          workspace.defaultForUserIds.includes(userIdString)
        ) {
          return {
            ...workspace,
            defaultForUserIds: without(selectedWorkspace?.defaultForUserIds, userIdString),
          };
        }
        return workspace;
      });
      setState({ currentWorkspaces: newList });
    },

  updateWorkspaceAnalyzerTovDefault:
    (generationTool, customTovId) =>
    async ({ dispatch, setState }) => {
      const currentWorkspace = dispatch(ACTIONS.getCurrentWorkspace());

      setState({ updatingWorkspaceAnalyzerTovDefault: true });
      try {
        const customTov = await updateWorkspaceAnalyzerTovDefault({
          generationTool,
          customTovId,
          workspaceId: currentWorkspace.id,
        });

        const workspaceDefaultTovAnalyzerForTool = currentWorkspace.defaultTovAnalyzerForTool || [];

        let newList = [];
        if (map(workspaceDefaultTovAnalyzerForTool, 'generationTool').includes(generationTool)) {
          newList = workspaceDefaultTovAnalyzerForTool.map((item) =>
            item.generationTool === generationTool
              ? { ...item, customTovId: customTov.id, customTovName: customTov.name }
              : item
          );
        } else {
          const newItem = {
            generationTool,
            customTovId: customTov.id,
            customTovName: customTov.name,
          };
          newList = [...workspaceDefaultTovAnalyzerForTool, newItem];
        }

        const newWorkspace = {
          ...currentWorkspace,
          defaultTovAnalyzerForTool: newList,
        };

        dispatch(PRIVATE_ACTIONS.mergeWorkspaceInCache(newWorkspace));
      } finally {
        setState({ updatingWorkspaceAnalyzerTovDefault: false });
      }
    },
};

export const WorkspaceStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: NAME,
});
